import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import styles from './UserList.module.css';
import Button from 'components/Button/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faTrash,
    faKey,
    faEdit,
    faUpload,
    faTimes,
    faEye,
    faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import { api } from 'api/axios';
import SearchInput from 'components/SearchInput/SearchInput';
import InputWithLabel from 'components/InputWithLabel/InputWithLabel';
import Loading from 'components/Loading/Loading';
import { studentSchema, teacherSchema } from '../../types/userSchemas';
import Papa from 'papaparse';

interface User {
    id: string;
    full_name: string;
    username: string;
    email?: string;
    contact?: string;
    subject?: string;
    class?: string;
    grade?: string;
    section?: string;
}

const UserList: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'teacher' | 'student'>('student');
    const [searchQuery, setSearchQuery] = useState('');
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const navigate = useNavigate();

    // Bulk Upload States
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState<{ successCount: number; errors: any[]; skippedCount?: number; isValidationOnly?: boolean; createdUsers?: any[]; skippedUsers?: any[] } | null>(null);

    // Password Modal State
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);

    const fetchUsers = async () => {
        setUsers([]);
        setLoading(true);
        try {
            const endpoint = activeTab === 'student' ? '/students' : '/teachers';
            const response = await api.get(endpoint);
            setUsers(response.data.data || []);
        } catch (error) {
            console.error(`Error fetching ${activeTab}s:`, error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [activeTab]);

    const handleAddUser = () => {
        navigate('/user-management/add');
    };

    const handleEdit = (id: string) => {
        navigate(`/user-management/edit/${id}?role=${activeTab}`);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setUploadFile(e.target.files[0]);
        }
    };

    const handleBulkUpload = async () => {
        if (!uploadFile) return;

        setUploading(true);
        setUploadResult(null);

        // 1. Frontend validation (Check file extension)
        if (!uploadFile.name.endsWith('.csv')) {
            setUploadResult({ successCount: 0, errors: [{ row: 'File', message: 'Please upload a valid .csv file.' }], isValidationOnly: true });
            setUploading(false);
            return;
        }

        const fileContent = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target?.result as string || '');
            reader.readAsText(uploadFile);
        });

        const parsedData = await new Promise<any>((resolve, reject) => {
            Papa.parse(fileContent, {
                header: true,
                skipEmptyLines: true,
                complete: (results: any) => resolve(results),
                error: (error: any) => reject(error)
            });
        }).catch(err => {
            setUploadResult({ successCount: 0, errors: [{ row: 'Parse', message: 'Failed to parse CSV file.' }], isValidationOnly: true });
            return null;
        });

        if (!parsedData) {
            setUploading(false);
            return;
        }
        
        const data = parsedData.data as any[];

        if (data.length === 0) {
            setUploadResult({ successCount: 0, errors: [{ row: 'File', message: 'CSV file is empty or contains only headers.' }], isValidationOnly: true });
            setUploading(false);
            return;
        }

        const MAX_USERS = 500;
        if (data.length > MAX_USERS) {
            setUploadResult({ successCount: 0, errors: [{ row: 'Limit', message: `For performance and stability, please limit bulk uploads to a maximum of ${MAX_USERS} users at a time.` }], isValidationOnly: true });
            setUploading(false);
            return;
        }

        const headers = parsedData.meta.fields?.map((h: string) => h.trim().toLowerCase()) || [];
        const hasFullName = headers.some((h: string) => ['full name', 'fullname', 'full_name'].includes(h));
        const hasUsername = headers.some((h: string) => ['username', 'user name'].includes(h));
        const hasPassword = headers.some((h: string) => ['password', 'pass'].includes(h));
        const hasGrade = headers.some((h: string) => ['grade', 'class'].includes(h));
        const hasSection = headers.some((h: string) => ['section', 'sec'].includes(h));

        if (activeTab === 'student') {
            if (!hasFullName || !hasUsername || !hasPassword || !hasGrade || !hasSection) {
                setUploadResult({ successCount: 0, errors: [{ row: 'Format', message: 'Invalid CSV format. Please ensure it contains all required exact headers: "Full Name", "Username", "Password", "Grade", and "Section". No typos allowed.' }], isValidationOnly: true });
                setUploading(false);
                return;
            }
        } else {
            if (!hasFullName || !hasUsername || !hasPassword) {
                setUploadResult({ successCount: 0, errors: [{ row: 'Format', message: 'Invalid CSV format. Please ensure it contains all required exact headers: "Full Name", "Username", and "Password". No typos allowed.' }], isValidationOnly: true });
                setUploading(false);
                return;
            }
        }

        const frontendErrors: any[] = [];
        for (let i = 0; i < data.length; i++) {
            const row = data[i];
            
            const getVal = (keys: string[]) => {
                const key = Object.keys(row).find(k => keys.includes(k.trim().toLowerCase()));
                return key ? row[key]?.trim() : '';
            }

            const fullName = getVal(['full name', 'fullname', 'full_name']);
            const username = getVal(['username', 'user name']);
            const password = getVal(['password', 'pass']);
            const grade = getVal(['grade', 'class']);
            const section = getVal(['section', 'sec']);

            if (activeTab === 'student') {
                const studentData = {
                    full_name: fullName,
                    username: username,
                    password: password,
                    grade: grade,
                    section: section,
                    role: 'student' as const
                };

                const validationResult = studentSchema.safeParse(studentData);
                
                if (!validationResult.success) {
                    validationResult.error.issues.forEach(issue => {
                        frontendErrors.push({ row: i + 2, username: username || 'Unknown', message: issue.message });
                    });
                }
            } else {
                const teacherData = {
                    full_name: fullName,
                    username: username,
                    password: password,
                    role: 'teacher' as const
                };

                const validationResult = teacherSchema.safeParse(teacherData);
                if (!validationResult.success) {
                    validationResult.error.issues.forEach(issue => {
                        frontendErrors.push({ row: i + 2, username: username || 'Unknown', message: issue.message });
                    });
                }
            }
        }

        if (frontendErrors.length > 0) {
            setUploadResult({ successCount: 0, errors: frontendErrors, isValidationOnly: true });
            setUploading(false);
            return;
        }

        try {
            const formData = new FormData();
            formData.append('file', uploadFile);

            const baseEndpoint = activeTab === 'student' ? '/students/bulk-upload' : '/teachers/bulk-upload';
            
            // 2. Validation Layer API Call
            const validationRes = await api.post(`${baseEndpoint}?validateOnly=true`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            const validationData = validationRes.data.data;
            if (validationData.errors && validationData.errors.length > 0) {
                // Display validation errors and abort actual upload
                setUploadResult({ successCount: 0, errors: validationData.errors, isValidationOnly: true });
                setUploading(false);
                return;
            }

            // 3. Actual Upload (if validation passed)
            const res = await api.post(baseEndpoint, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            setUploadResult({ ...res.data.data, isValidationOnly: false });
            if (res.data.data.successCount > 0) {
                fetchUsers();
            }
            // Do not auto-close the modal; let the user review the UI results.
        } catch (error: any) {
            console.error('Bulk upload error:', error);
            setUploadResult({ successCount: 0, errors: [{ row: 'API', message: error.response?.data?.message || 'Error uploading file' }], isValidationOnly: true });
        } finally {
            setUploading(false);
        }
    };

    const handleCloseModal = () => {
        setIsBulkModalOpen(false);
        setUploadFile(null);
        setUploadResult(null);
    };

    const handleDeleteUser = async (id: string, name: string) => {
        if (window.confirm(`Are you sure you want to delete the user ${name}?`)) {
            try {
                await api.delete(`${activeTab === 'student' ? '/students' : '/teachers'}/${id}`);
                fetchUsers();
            } catch (error: any) {
                alert(error.response?.data?.message || 'Failed to delete user');
            }
        }
    };

    const handleOpenPasswordModal = (id: string) => {
        setSelectedUserId(id);
        setIsPasswordModalOpen(true);
    };

    const handleClosePasswordModal = () => {
        setIsPasswordModalOpen(false);
        setSelectedUserId(null);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordError(null);
    };

    const handleUpdatePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordError("New passwords do not match");
            return;
        }

        setIsSubmittingPassword(true);
        setPasswordError(null);
        try {
            await api.patch(`${activeTab === 'student' ? '/students' : '/teachers'}/${selectedUserId}/change-password`, {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });
            alert('Password changed successfully!');
            handleClosePasswordModal();
        } catch (error: any) {
            setPasswordError(error.response?.data?.message || 'Failed to change password');
        } finally {
            setIsSubmittingPassword(false);
        }
    };

    const studentCsvTemplate = "data:text/csv;charset=utf-8,Full Name,Username,Password,Grade,Section%0AJohn Doe,johndoe,password123,10,A";
    const teacherCsvTemplate = "data:text/csv;charset=utf-8,Full Name,email,Username,Password%0AJohn Doe,john@test.com,johndoe,password123";

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, activeTab]);

    const filteredUsers = users.filter(user =>
        user.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.username?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const totalItems = filteredUsers.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
    const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

    const renderPageNumbers = () => {
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            pages.push(
                <button
                    key={i}
                    className={`${styles.pageBtn} ${currentPage === i ? styles.activePage : ''}`}
                    onClick={() => setCurrentPage(i)}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    return (
        <div className={styles.pageContainer}>
            <div className={styles.pageHeader}>
                <h1 className={styles.pageTitle}>User Management</h1>
                <div className={styles.headerActions}>
                    <div style={{ display: 'inline-block' }}>
                        <Button 
                            variant="secondary" 
                            size="md" 
                            onClick={() => setIsBulkModalOpen(true)}
                        >
                            <FontAwesomeIcon icon={faUpload} style={{ marginRight: '8px' }} /> Bulk Upload
                        </Button>
                    </div>
                    <Button variant="primary" size="md" onClick={handleAddUser} className={styles.addBtn}>
                        <FontAwesomeIcon icon={faPlus} style={{ marginRight: '8px' }} /> Add New User
                    </Button>
                </div>
            </div>

            <div className={styles.tableCard}>
                <div className={styles.tabsContainer}>
                    <button
                        className={`${styles.tabBtn} ${activeTab === 'teacher' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('teacher')}
                    >
                        Teachers
                    </button>
                    <button
                        className={`${styles.tabBtn} ${activeTab === 'student' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('student')}
                    >
                        Students
                    </button>
                    {/* Add Search Input inside the tabs row if desired, or keep it separate */}
                    <div className={styles.searchWrapper}>
                        <SearchInput
                            value={searchQuery}
                            onChange={setSearchQuery}
                            placeholder={`Search ${activeTab}s`}
                        />
                    </div>
                </div>
                {loading ? (
                    <Loading message={`Loading ${activeTab}s...`} />
                ) : filteredUsers.length > 0 ? (
                    <table className={styles.table}>
                        <thead>
                            {activeTab === 'teacher' ? (
                                <tr>
                                    <th className={styles.sno}>S.NO</th>
                                    <th>FULL NAME</th>
                                    <th>USERNAME</th>
                                    <th>ACTIONS</th>
                                </tr>
                            ) : (
                                <tr>
                                    <th className={styles.sno}>S.NO</th>
                                    <th>FULL NAME</th>
                                    <th>USERNAME</th>
                                    <th>STUDENT GRADE</th>
                                    <th>GRADE SECTION</th>
                                    <th>ACTIONS</th>
                                </tr>
                            )}
                        </thead>
                        <tbody>
                            {paginatedUsers.map((user, index) => (
                                <tr key={user.id}>
                                    <td className={styles.sno}>{startIndex + index + 1}</td>
                                    <td>{user.full_name}</td>
                                    <td>{user.username}</td>
                                    {activeTab === 'teacher' ? (
                                        <></>
                                    ) : (
                                        <>
                                            <td>{user.grade || ''}</td>
                                            <td>{user.section || ''}</td>
                                        </>
                                    )}
                                    <td>
                                        <div className={styles.actionsBox}>
                                            <button
                                                className={`${styles.actionBtn} ${styles.editBtn}`}
                                                title="Edit User"
                                                onClick={() => handleEdit(user.id)}
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button 
                                                className={`${styles.actionBtn} ${styles.pwdBtn}`} 
                                                title="Reset Password"
                                                onClick={() => handleOpenPasswordModal(user.id)}
                                            >
                                                <FontAwesomeIcon icon={faKey} />
                                            </button>
                                            <button 
                                                className={`${styles.actionBtn} ${styles.deleteBtn}`} 
                                                title="Delete User"
                                                onClick={() => handleDeleteUser(user.id, user.full_name)}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className={styles.emptyState}>
                        No {activeTab}s found matching your search criteria.
                    </div>
                )}
                
                {/* Pagination Footer */}
                {!loading && filteredUsers.length > 0 && (
                    <div className={styles.paginationFooter}>
                        <div className={styles.paginationInfo}>
                            Showing {startIndex + 1} to {endIndex} of {totalItems} {activeTab}s
                        </div>
                        <div className={styles.paginationControls}>
                            <button 
                                className={styles.prevNextBtn} 
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            {renderPageNumbers()}
                            <button 
                                className={styles.prevNextBtn} 
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Bulk Upload Modal */}
            {isBulkModalOpen && (
                <div className={styles.modalOverlay} onClick={handleCloseModal}>
                    <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>Bulk Upload {activeTab === 'student' ? 'Students' : 'Teachers'}</h2>
                            <button className={styles.closeBtn} onClick={handleCloseModal}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <p>Upload a CSV file containing the user details. Make sure to match the expected format.</p>
                            
                            <div className={styles.fileInput}>
                                <input 
                                    type="file" 
                                    accept=".csv" 
                                    onChange={handleFileChange}
                                    id="bulkUploadFile"
                                />
                            </div>

                            <a 
                                href={activeTab === 'student' ? studentCsvTemplate : teacherCsvTemplate} 
                                download={activeTab === 'student' ? "student_template.csv" : "teacher_template.csv"}
                                className={styles.templateLink}
                            >
                                Download Sample CSV Template
                            </a>

                            {uploadResult && (
                                <div style={{ marginTop: '16px', padding: '12px', borderRadius: '8px', background: uploadResult.errors.length > 0 ? '#FEF2F2' : '#ECFDF5' }}>
                                    <h4 style={{ margin: 0, color: uploadResult.errors.length > 0 ? '#991B1B' : '#065F46' }}>
                                        {uploadResult.errors.length > 0
                                            ? `Validation failed with ${uploadResult.errors.length} errors. Please fix them and re-upload.`
                                            : (uploadResult.successCount === 0 ? 'No users were created.' : `Successfully created ${uploadResult.successCount} users!`) + (uploadResult.skippedCount ? ` (${uploadResult.skippedCount} skipped due to existing usernames)` : '')}
                                    </h4>
                                    {uploadResult.errors && uploadResult.errors.length > 0 && (
                                        <div style={{ marginTop: '8px', maxHeight: '150px', overflowY: 'auto', fontSize: '14px', color: '#DC2626' }}>
                                            {uploadResult.errors.map((err, idx) => (
                                                <div key={idx} style={{ marginBottom: '4px' }}>
                                                    <strong>Row {err.row} ({err.username || 'N/A'}):</strong> {err.message}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {uploadResult.errors.length === 0 && uploadResult.createdUsers && uploadResult.createdUsers.length > 0 && (
                                        <div style={{ marginTop: '12px' }}>
                                            <strong style={{ color: '#065F46' }}>Created Accounts:</strong>
                                            <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px', maxHeight: '100px', overflowY: 'auto', fontSize: '14px', color: '#065F46' }}>
                                                {uploadResult.createdUsers.map((u, idx) => (
                                                    <li key={idx}>{u.fullName} ({u.username})</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {uploadResult.errors.length === 0 && uploadResult.skippedUsers && uploadResult.skippedUsers.length > 0 && (
                                        <div style={{ marginTop: '12px' }}>
                                            <strong style={{ color: '#92400E' }}>Skipped Accounts:</strong>
                                            <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px', maxHeight: '100px', overflowY: 'auto', fontSize: '14px', color: '#92400E' }}>
                                                {uploadResult.skippedUsers.map((u, idx) => (
                                                    <li key={idx}>Row {u.row}: {u.username} - {u.reason}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className={styles.modalFooter}>
                            <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
                            <Button 
                                variant="primary" 
                                onClick={handleBulkUpload} 
                                disabled={!uploadFile || uploading}
                            >
                                {uploading ? 'Uploading...' : 'Upload Users'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Password Modal */}
            {isPasswordModalOpen && (
                <div className={styles.modalOverlay} onClick={handleClosePasswordModal}>
                    <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>Update Password</h2>
                            <button className={styles.closeBtn} type="button" onClick={handleClosePasswordModal}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleUpdatePassword}>
                            <div className={styles.modalBody}>
                                {passwordError && <div style={{ color: '#DC2626', marginBottom: '16px', fontSize: '14px' }}>{passwordError}</div>}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <InputWithLabel
                                        label="Your Admin Password:"
                                        type={showCurrentPassword ? "text" : "password"}
                                        placeholder="Enter your admin password"
                                        required
                                        value={passwordData.currentPassword}
                                        onChange={(e: any) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                                        iconRight={
                                            <FontAwesomeIcon 
                                                icon={showCurrentPassword ? faEyeSlash : faEye} 
                                                style={{ cursor: 'pointer', color: '#6B7280' }}
                                                onClick={() => setShowCurrentPassword(!showCurrentPassword)} 
                                            />
                                        }
                                    />
                                    <InputWithLabel
                                        label="New Password:"
                                        type={showNewPassword ? "text" : "password"}
                                        placeholder="Enter new password for user"
                                        required
                                        value={passwordData.newPassword}
                                        onChange={(e: any) => setPasswordData({...passwordData, newPassword: e.target.value})}
                                        iconRight={
                                            <FontAwesomeIcon 
                                                icon={showNewPassword ? faEyeSlash : faEye} 
                                                style={{ cursor: 'pointer', color: '#6B7280' }}
                                                onClick={() => setShowNewPassword(!showNewPassword)} 
                                            />
                                        }
                                    />
                                    <InputWithLabel
                                        label="Confirm New Password:"
                                        type={showConfirmPassword ? "text" : "password"}
                                        placeholder="Confirm new password"
                                        required
                                        value={passwordData.confirmPassword}
                                        onChange={(e: any) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                                        iconRight={
                                            <FontAwesomeIcon 
                                                icon={showConfirmPassword ? faEyeSlash : faEye} 
                                                style={{ cursor: 'pointer', color: '#6B7280' }}
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)} 
                                            />
                                        }
                                    />
                                </div>
                            </div>
                            <div className={styles.modalFooter}>
                                <Button type="button" variant="secondary" onClick={handleClosePasswordModal}>Cancel</Button>
                                <Button type="submit" variant="primary" isLoading={isSubmittingPassword}>
                                    Update Password
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserList;
